import React from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Link } from 'react-router-dom';
import { services, contactInfo } from '../data/mock';

const Services = () => {
  const IconComponent = ({ iconName }) => {
    const Icon = LucideIcons[iconName.split('-').map((word, i) => 
      i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
    ).join('').replace(/^\w/, c => c.toUpperCase())];
    return Icon ? <Icon size={28} /> : <LucideIcons.Star size={28} />;
  };

  const serviceDetails = [
    {
      id: 1,
      benefits: [
        "Customized marketing roadmap aligned with business goals",
        "Competitive analysis and market positioning",
        "Multi-channel campaign planning and execution",
        "Performance tracking and optimization"
      ]
    },
    {
      id: 2,
      benefits: [
        "Content creation and curation for all platforms",
        "Community management and engagement",
        "Influencer partnerships and collaborations",
        "Social media advertising campaigns"
      ]
    },
    {
      id: 3,
      benefits: [
        "Google Ads, Facebook Ads, LinkedIn Ads management",
        "Campaign strategy and budget optimization",
        "A/B testing and conversion rate optimization",
        "Detailed ROI reporting and analytics"
      ]
    },
    {
      id: 4,
      benefits: [
        "Technical SEO audit and optimization",
        "Keyword research and content strategy",
        "On-page and off-page SEO",
        "Local SEO and Google Business Profile optimization"
      ]
    },
    {
      id: 5,
      benefits: [
        "Landing page design and optimization",
        "Sales funnel creation and testing",
        "User experience (UX) improvements",
        "Conversion rate optimization (CRO)"
      ]
    },
    {
      id: 6,
      benefits: [
        "Brand strategy and positioning",
        "Content marketing and storytelling",
        "PR and media outreach",
        "Reputation management"
      ]
    },
    {
      id: 7,
      benefits: [
        "Author brand building and platform development",
        "Targeted reader acquisition campaigns",
        "Amazon and online bookstore optimization",
        "Book launch strategies and promotional campaigns",
        "Email marketing to engaged reader lists"
      ]
    },
    {
      id: 8,
      benefits: [
        "Custom Shopify store design and development",
        "Product page optimization for conversions",
        "Shopify SEO and marketing automation",
        "Email marketing and abandoned cart recovery",
        "Performance tracking and analytics setup"
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0D234E] to-[#1a3a6e] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Our Services
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Comprehensive digital marketing solutions designed to drive growth, increase visibility, and maximize your return on investment
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => {
              const details = serviceDetails.find(d => d.id === service.id);
              return (
                <div
                  key={service.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="w-16 h-16 bg-[#0D234E] rounded-xl flex items-center justify-center text-[#BFB47E] mb-6">
                      <IconComponent iconName={service.icon} />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-[#0D234E] mb-4">
                      {service.title}
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-6 text-lg">
                      {service.description}
                    </p>
                    <div className="space-y-3 mb-8">
                      {details?.benefits.map((benefit, i) => (
                        <div key={i} className="flex items-start space-x-3">
                          <CheckCircle className="text-[#BFB47E] flex-shrink-0 mt-1" size={20} />
                          <span className="text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                    <Link
                      to="/contact"
                      className="inline-flex items-center text-[#0D234E] font-semibold hover:text-[#BFB47E] transition-colors"
                    >
                      Get Started
                      <ArrowRight size={20} className="ml-2" />
                    </Link>
                  </div>
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <div className="relative">
                      <div className="absolute inset-0 bg-[#BFB47E] rounded-2xl transform rotate-3 opacity-20"></div>
                      <img
                        src={`https://images.unsplash.com/photo-${
                          [
                            '1696041754237-e0c1ffd10138',
                            '1730382624709-81e52dd294d4',
                            '1460925895917-afdab827c52f',
                            '1543286386-2e659306cd6c',
                            '1587614313085-5da51cebd8ac',
                            '1762968280286-0bfcc4afd0ea',
                            '1512820790803-83ca734da794',
                            '1472851294608-062f824d29cc'
                          ][index]
                        }`}
                        alt={service.title}
                        className="relative z-10 rounded-2xl shadow-xl w-full h-[400px] object-cover"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0D234E] mb-4">
              Our Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven approach to deliver exceptional results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery',
                description: 'We analyze your business, goals, and target audience to create a tailored strategy'
              },
              {
                step: '02',
                title: 'Strategy',
                description: 'Develop a comprehensive marketing plan with clear objectives and KPIs'
              },
              {
                step: '03',
                title: 'Execution',
                description: 'Implement campaigns across chosen channels with expert precision'
              },
              {
                step: '04',
                title: 'Optimize',
                description: 'Continuously monitor, test, and refine for maximum performance'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-[#0D234E] text-[#BFB47E] rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-[#0D234E] mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#0D234E] to-[#1a3a6e] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Let's discuss which services will drive the best results for you
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-[#BFB47E] text-[#0D234E] px-8 py-4 rounded-md font-semibold hover:bg-[#d4c991] transition-all duration-200 hover:shadow-xl inline-flex items-center justify-center"
            >
              Book a Consultation
              <ArrowRight size={20} className="ml-2" />
            </Link>
            <a
              href={contactInfo.whatsapp}
              data-testid="services-contact-whatsapp"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-md font-semibold hover:bg-white hover:text-[#0D234E] transition-all duration-200 inline-flex items-center justify-center"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
